/* Clinical Kits landing page (sections/mt-ck-*.liquid).
   Part 1 is the original page's animations.js runtime (reveal, bento
   spotlight, review-card sparks), unchanged except that the accent colour is
   read from the page wrapper. Part 2 re-implements the original React page
   behaviour in plain JS. */
/* Every mt-ck-* section includes this file; run it once per page. */
if (!window.__ckLpLoaded) {
window.__ckLpLoaded = true;
(function () {
  "use strict";

  // ──────────────────────────────────────────────────────────────
  // Utilities
  // ──────────────────────────────────────────────────────────────
  const ACCENT_RGB = () => {
    // read --accent so animations recolor when Tweaks change the accent
    const hex = getComputedStyle(document.querySelector(".ck-lp") || document.documentElement)
      .getPropertyValue("--accent").trim().replace("#", "");
    const x = hex.length === 3 ? hex.replace(/./g, c => c + c) : hex;
    if (!x) return [167, 139, 250]; // default violet
    return [parseInt(x.slice(0, 2), 16), parseInt(x.slice(2, 4), 16), parseInt(x.slice(4, 6), 16)];
  };

  // ──────────────────────────────────────────────────────────────
  // 1. Typewriter (hero h1)
  // ──────────────────────────────────────────────────────────────
  // Looks for [data-typewriter] elements with a data-text attribute,
  // types it character-by-character, then fires "tw:done" so the
  // bold tagline beneath can reveal.
  window.MT_initTypewriter = function (root = document) {
    root.querySelectorAll("[data-typewriter]:not([data-tw-on])").forEach((el) => {
      el.dataset.twOn = "1";
      const text = el.dataset.text || el.textContent;
      const speed = parseInt(el.dataset.speed || "38", 10);
      const initial = parseInt(el.dataset.initialDelay || "300", 10);
      el.innerHTML = "";
      const span = document.createElement("span");
      span.className = "mt-tw-content";
      const cursor = document.createElement("span");
      cursor.className = "mt-tw-cursor";
      cursor.textContent = "|";
      el.append(span, cursor);

      let i = 0;
      let target = span; // chars normally land in `span`; "{…}" redirects into an emphasis span
      const tick = () => {
        if (i < text.length) {
          const ch = text[i++];
          // "|" in data-text means: insert a hard line break here.
          // "{ … }" wraps the enclosed run in an emphasis span (extra sheen).
          if (ch === "|") {
            span.appendChild(document.createElement("br"));
          } else if (ch === "{") {
            const em = document.createElement("span");
            em.className = "mt-tw-em";
            span.appendChild(em);
            target = em;
          } else if (ch === "}") {
            target = span;
          } else {
            target.appendChild(document.createTextNode(ch));
          }
          setTimeout(tick, speed);
        } else {
          setTimeout(() => cursor.classList.add("is-done"), 900);
          el.dispatchEvent(new CustomEvent("tw:done", { bubbles: true }));
        }
      };
      // Start typing once the heading scrolls into view (so below-fold
      // typewriters animate when the user reaches them, not on load).
      const start = () => setTimeout(tick, initial);
      if ("IntersectionObserver" in window) {
        const obs = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { obs.disconnect(); start(); }
        }, { threshold: 0.4 });
        obs.observe(el);
      } else {
        start();
      }
    });
  };

  // ──────────────────────────────────────────────────────────────
  // Word-by-word reveal: splits [data-word-reveal] text into words
  // that fade + rise in sequence once scrolled into view.
  // ──────────────────────────────────────────────────────────────
  window.MT_initWordReveal = function (root = document) {
    root.querySelectorAll("[data-word-reveal]:not([data-wr-on])").forEach((el) => {
      el.dataset.wrOn = "1";
      const words = (el.textContent || "").trim().split(/\s+/);
      const wDelay = parseInt(el.dataset.wrDelay || "45", 10);
      el.textContent = "";
      words.forEach((w, i) => {
        const span = document.createElement("span");
        span.className = "mt-wr-word";
        span.textContent = w;
        span.style.transitionDelay = `${i * wDelay}ms`;
        el.appendChild(span);
        el.appendChild(document.createTextNode(" "));
      });
      const reveal = () => el.classList.add("is-in");
      if ("IntersectionObserver" in window) {
        const obs = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { obs.disconnect(); reveal(); }
        }, { threshold: 0.3 });
        obs.observe(el);
      } else { reveal(); }
    });
  };

  // ──────────────────────────────────────────────────────────────
  // 2. Reveal on scroll (.mt-reveal)
  // ──────────────────────────────────────────────────────────────
  window.MT_initReveal = function (root = document) {
    if (!("IntersectionObserver" in window)) {
      root.querySelectorAll(".mt-reveal").forEach(el => el.classList.add("is-in"));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("is-in");
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -36px 0px" });
    root.querySelectorAll(".mt-reveal:not(.is-in)").forEach((el) => obs.observe(el));
  };

  // ──────────────────────────────────────────────────────────────
  // 3. Count-up numbers ([data-countup])
  // ──────────────────────────────────────────────────────────────
  // Continuous bento-style drifting particles while hovering a card,
  // same look as the Pass-or-Don't-Pay "Instant Impact" cards (.mt-bento-particle).
  window.MT_revParticles = function (card, action) {
    if (!card) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (action === "start") {
      if (card._revOn) return;
      card._revOn = true;
      const [r, g, b] = ACCENT_RGB();
      const spawn = () => {
        if (!card._revOn) return;
        for (let i = 0; i < 5; i++) {
          const p = document.createElement("div");
          p.className = "mt-bento-particle";
          p.style.background = `rgba(${r},${g},${b},1)`;
          p.style.boxShadow = `0 0 5px rgba(${r},${g},${b},.6)`;
          p.style.left = Math.random() * card.clientWidth + "px";
          p.style.top = Math.random() * card.clientHeight + "px";
          card.appendChild(p);
          const dx = (Math.random() - 0.5) * 30;
          const dy = (Math.random() - 0.5) * 30;
          p.animate(
            [{ transform: "translate(0,0)", opacity: 1 },
             { transform: `translate(${dx}px,${dy}px)`, opacity: 0 }],
            { duration: 1800 + Math.random() * 800, easing: "ease-out", fill: "forwards" }
          );
          setTimeout(() => p.remove(), 2600);
        }
        card._revTimer = setTimeout(spawn, 650);
      };
      spawn();
    } else {
      card._revOn = false;
      clearTimeout(card._revTimer);
    }
  };

  // Spark burst from a given anchor element, relative to a positioned host.
  // Re-fires on every call (throttled by `gap` ms). Used on review-card hover.
  window.MT_sparkBurst = function (host, anchor, gap = 700) {
    if (!host || !anchor) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const now = Date.now();
    if (host._lastSpark && now - host._lastSpark < gap) return;
    host._lastSpark = now;
    const hr = host.getBoundingClientRect();
    const ar = anchor.getBoundingClientRect();
    const cx = ar.left - hr.left + ar.width / 2;
    const cy = ar.top - hr.top + ar.height / 2;
    const N = 12;
    for (let i = 0; i < N; i++) {
      const sp = document.createElement("span");
      sp.className = "pdp2-spark";
      const ang = (Math.PI * 2 * i) / N + (Math.random() - 0.5) * 0.5;
      const dist = 26 + Math.random() * 34;
      sp.style.setProperty("--x", cx + "px");
      sp.style.setProperty("--y", cy + "px");
      sp.style.setProperty("--dx", Math.cos(ang) * dist + "px");
      sp.style.setProperty("--dy", Math.sin(ang) * dist + "px");
      sp.style.setProperty("--dur", (0.6 + Math.random() * 0.4) + "s");
      const sc = 0.5 + Math.random() * 0.8;
      sp.style.width = sp.style.height = (6 * sc) + "px";
      host.appendChild(sp);
      setTimeout(() => sp.remove(), 1100);
    }
  };

  // Spawn a short radial particle burst from a stat chip.
  window.MT_burstChip = function (chip) {
    if (!chip || chip.dataset.burst === "1") return;
    chip.dataset.burst = "1";
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = chip.getBoundingClientRect();
    const cx = r.width / 2, cy = r.height / 2;
    const N = 14;
    for (let i = 0; i < N; i++) {
      const sp = document.createElement("span");
      sp.className = "pdp2-spark";
      const ang = (Math.PI * 2 * i) / N + (Math.random() - 0.5) * 0.5;
      const dist = 34 + Math.random() * 40;
      sp.style.setProperty("--x", cx + "px");
      sp.style.setProperty("--y", cy + "px");
      sp.style.setProperty("--dx", Math.cos(ang) * dist + "px");
      sp.style.setProperty("--dy", Math.sin(ang) * dist + "px");
      sp.style.setProperty("--dur", (0.7 + Math.random() * 0.5) + "s");
      const sc = 0.5 + Math.random() * 0.9;
      sp.style.width = sp.style.height = (6 * sc) + "px";
      chip.appendChild(sp);
      setTimeout(() => sp.remove(), 1300);
    }
    chip.classList.add("is-bursting");
    setTimeout(() => chip.classList.remove("is-bursting"), 700);
  };

  // Immediately animate any [data-countup] inside `root` (no scroll needed).
  // Used when content is revealed by an accordion/tab so it can't rely on
  // the element having scrolled into view.
  window.MT_runCountUp = function (root = document) {
    root.querySelectorAll("[data-countup]").forEach((el, idx) => {
      if (el.dataset.cuDone === "1") return;
      el.dataset.cuDone = "1";
      el.dataset.cuOn = "1"; // stop the IO-based init from double-running
      const to = parseFloat(el.dataset.to || "0");
      const from = parseFloat(el.dataset.from || "0");
      const dur = parseFloat(el.dataset.duration || "2") * 1000;
      const chip = el.closest(".pdp2-statchip");
      const begin = () => {
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / dur, 1);
          const eased = 1 - Math.pow(1 - t, 4);
          el.textContent = String(Math.round(from + (to - from) * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      };
      setTimeout(begin, idx * 160);
    });
  };

  window.MT_initCountUp = function (root = document) {
    root.querySelectorAll("[data-countup]:not([data-cu-on])").forEach((el) => {
      el.dataset.cuOn = "1";
      const to = parseFloat(el.dataset.to || "0");
      const from = parseFloat(el.dataset.from || "0");
      const dur = parseFloat(el.dataset.duration || "2") * 1000;
      el.textContent = String(Math.round(from));
      const obs = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / dur, 1);
          const eased = 1 - Math.pow(1 - t, 4); // power3.out
          el.textContent = String(Math.round(from + (to - from) * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: 0.2 });
      obs.observe(el);
    });
  };

  // ──────────────────────────────────────────────────────────────
  // 4. Magic Bento: spotlight + tilt + magnetism + particle burst
  // ──────────────────────────────────────────────────────────────
  // Applied to .mt-bento grids; each direct .mt-bento-card child gets
  // tilt on mousemove, a global spotlight follows the cursor across
  // the grid, and clicking a card emits a radial ripple.
  const PROX = 60, FADE = 80, RADIUS = 220;
  let spotEl = null;
  const ensureSpot = () => {
    if (spotEl) return spotEl;
    spotEl = document.createElement("div");
    spotEl.className = "mt-bento-spot";
    document.body.appendChild(spotEl);
    return spotEl;
  };

  window.MT_initBento = function (root = document) {
    root.querySelectorAll(".mt-bento").forEach((grid) => {
      // Re-init safe: drop the previous document-level handler so a fresh
      // card NodeList is captured (filter/sort re-renders swap card nodes).
      if (grid.__btMove) document.removeEventListener("mousemove", grid.__btMove);
      grid.dataset.bentoOn = "1";
      const spot = ensureSpot();
      const cards = grid.querySelectorAll(".mt-bento-card");

      cards.forEach((card) => {
        if (card.__btOn) return; // per-card listeners attach once only
        card.__btOn = true;
        let hovering = false;
        const particles = [];

        const spawn = () => {
          if (!hovering) return;
          for (let i = 0; i < 6; i++) {
            const p = document.createElement("div");
            p.className = "mt-bento-particle";
            const [r, g, b] = ACCENT_RGB();
            p.style.background = `rgba(${r},${g},${b},1)`;
            p.style.boxShadow = `0 0 5px rgba(${r},${g},${b},.6)`;
            const x = Math.random() * card.clientWidth;
            const y = Math.random() * card.clientHeight;
            p.style.left = x + "px";
            p.style.top = y + "px";
            card.appendChild(p);
            particles.push(p);
            const dx = (Math.random() - 0.5) * 30;
            const dy = (Math.random() - 0.5) * 30;
            p.animate(
              [{ transform: "translate(0,0)", opacity: 1 },
               { transform: `translate(${dx}px,${dy}px)`, opacity: 0 }],
              { duration: 1800 + Math.random() * 800, easing: "ease-out", fill: "forwards" }
            );
            setTimeout(() => { p.remove(); }, 2600);
          }
        };
        const clearParticles = () => {
          particles.splice(0).forEach((p) => p.remove());
        };

        card.addEventListener("mouseenter", () => {
          hovering = true;
          spawn();
        });
        card.addEventListener("mousemove", (e) => {
          const r = card.getBoundingClientRect();
          const x = e.clientX - r.left, y = e.clientY - r.top;
          const cx = r.width / 2, cy = r.height / 2;
          // tilt + slight magnetism via CSS vars; CSS rule on .mt-bento-card
          // reads them with a smooth transition
          card.style.setProperty("--tx", ((x - cx) * 0.04).toFixed(2) + "px");
          card.style.setProperty("--ty", ((y - cy) * 0.04).toFixed(2) + "px");
          card.style.setProperty("--rx", (((y - cy) / cy) * -7).toFixed(2) + "deg");
          card.style.setProperty("--ry", (((x - cx) / cx) * 7).toFixed(2) + "deg");
        });
        card.addEventListener("mouseleave", () => {
          hovering = false;
          clearParticles();
          card.style.setProperty("--glow-intensity", "0");
          card.style.setProperty("--tx", "0px");
          card.style.setProperty("--ty", "0px");
          card.style.setProperty("--rx", "0deg");
          card.style.setProperty("--ry", "0deg");
        });
        card.addEventListener("click", (e) => {
          const r = card.getBoundingClientRect();
          const x = e.clientX - r.left, y = e.clientY - r.top;
          const maxD = Math.max(
            Math.hypot(x, y),
            Math.hypot(x - r.width, y),
            Math.hypot(x, y - r.height),
            Math.hypot(x - r.width, y - r.height)
          );
          const [rd, gd, bd] = ACCENT_RGB();
          const rpl = document.createElement("div");
          rpl.style.cssText = `position:absolute;width:${maxD * 2}px;height:${maxD * 2}px;border-radius:50%;background:radial-gradient(circle,rgba(${rd},${gd},${bd},.3) 0%,rgba(${rd},${gd},${bd},.12) 35%,transparent 70%);left:${x - maxD}px;top:${y - maxD}px;pointer-events:none;z-index:5;`;
          card.appendChild(rpl);
          rpl.animate(
            [{ transform: "scale(0)", opacity: 1 },
             { transform: "scale(1)", opacity: 0 }],
            { duration: 700, easing: "ease-out", fill: "forwards" }
          );
          setTimeout(() => rpl.remove(), 700);
        });
      });

      // Document-level mousemove → spotlight follows cursor inside grid
      const onMove = (e) => {
        const gr = grid.getBoundingClientRect();
        const inside = e.clientX >= gr.left && e.clientX <= gr.right
                    && e.clientY >= gr.top  && e.clientY <= gr.bottom;
        if (!inside) {
          spot.style.opacity = "0";
          cards.forEach((c) => c.style.setProperty("--glow-intensity", "0"));
          return;
        }
        spot.style.left = e.clientX + "px";
        spot.style.top = e.clientY + "px";
        const [r, g, b] = ACCENT_RGB();
        spot.style.background = `radial-gradient(circle, rgba(${r},${g},${b},0.13) 0%, rgba(${r},${g},${b},0.07) 20%, rgba(${r},${g},${b},0.03) 40%, transparent 65%)`;

        let minD = Infinity;
        cards.forEach((card) => {
          const cr = card.getBoundingClientRect();
          const cx = cr.left + cr.width / 2, cy = cr.top + cr.height / 2;
          const d = Math.max(0, Math.hypot(e.clientX - cx, e.clientY - cy) - Math.max(cr.width, cr.height) / 2);
          minD = Math.min(minD, d);
          const intensity = d <= PROX ? 1 : d <= FADE ? (FADE - d) / (FADE - PROX) : 0;
          card.style.setProperty("--glow-x", ((e.clientX - cr.left) / cr.width * 100) + "%");
          card.style.setProperty("--glow-y", ((e.clientY - cr.top) / cr.height * 100) + "%");
          card.style.setProperty("--glow-intensity", intensity.toFixed(3));
          card.style.setProperty("--glow-radius", RADIUS + "px");
          card.style.setProperty("--glow-color", `${r},${g},${b}`);
        });
        spot.style.opacity = String(minD <= PROX ? 0.75 : minD <= FADE ? ((FADE - minD) / (FADE - PROX)) * 0.75 : 0);
      };
      grid.__btMove = onMove;
      document.addEventListener("mousemove", onMove);
    });
  };

  // ──────────────────────────────────────────────────────────────
  // 5. Border-glow (cursor-tracking conic mask) for .mt-glow-card
  // ──────────────────────────────────────────────────────────────
  // Sets two CSS vars on pointermove that the CSS rules in styles.css
  // consume to mask the gradient halo + edge light to the cursor side.
  window.MT_initGlowCards = function (root = document) {
    const getCx = (el) => {
      const r = el.getBoundingClientRect();
      return [r.width / 2, r.height / 2];
    };
    const edgeProx = (el, x, y) => {
      const [cx, cy] = getCx(el);
      const dx = x - cx, dy = y - cy;
      const kx = dx ? cx / Math.abs(dx) : Infinity;
      const ky = dy ? cy / Math.abs(dy) : Infinity;
      return Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
    };
    const cursorAngle = (el, x, y) => {
      const [cx, cy] = getCx(el);
      const dx = x - cx, dy = y - cy;
      if (!dx && !dy) return 0;
      let d = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      return d < 0 ? d + 360 : d;
    };
    root.querySelectorAll(".mt-glow-card:not([data-gc-on])").forEach((el) => {
      el.dataset.gcOn = "1";
      el.addEventListener("pointermove", (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left, y = e.clientY - r.top;
        el.style.setProperty("--edge-proximity", (edgeProx(el, x, y) * 100).toFixed(2));
        el.style.setProperty("--cursor-angle", cursorAngle(el, x, y).toFixed(2) + "deg");
      });
      el.addEventListener("pointerleave", () => el.style.setProperty("--edge-proximity", "0"));
    });
  };

  // ──────────────────────────────────────────────────────────────
  // 6. Sweep on card hover (.mt-sweep-card)
  // ──────────────────────────────────────────────────────────────
  // A horizontal accent-color light bar slides L→R on enter, R→L on leave.
  window.MT_initSweep = function (root = document) {
    root.querySelectorAll(".mt-sweep-card:not([data-sw-on])").forEach((card) => {
      card.dataset.swOn = "1";
      card.addEventListener("mouseenter", () => {
        card.classList.remove("is-sweep-rev");
        // force reflow so re-adding sweep class restarts animation
        void card.offsetWidth;
        card.classList.add("is-sweep-fwd");
      });
      card.addEventListener("mouseleave", () => {
        card.classList.remove("is-sweep-fwd");
        void card.offsetWidth;
        card.classList.add("is-sweep-rev");
        setTimeout(() => card.classList.remove("is-sweep-rev"), 1000);
      });
    });
  };

  // ──────────────────────────────────────────────────────────────
  // Global init: call after React mounts. Components.jsx pings this
  // on a microtask + once again after a short delay (so pages that
  // lazy-mount sections still get covered).
  // ──────────────────────────────────────────────────────────────
  window.MT_initAnimations = function () {
    window.MT_initTypewriter();
    window.MT_initWordReveal();
    window.MT_initReveal();
    window.MT_initCountUp();
    window.MT_initBento();
    window.MT_initGlowCards();
    window.MT_initSweep();
  };
})();

/* ── Part 2: page behaviour (vanilla port of the React page) ─────────── */
(function () {
  var RM = function () { return matchMedia('(prefers-reduced-motion: reduce)').matches; };
  var EASE = 'cubic-bezier(.3,.7,.2,1)';

  function once(el, fn, opts) {
    if (!('IntersectionObserver' in window)) { fn(); return; }
    var io = new IntersectionObserver(function (es) {
      if (es.some(function (e) { return e.isIntersecting; })) { io.disconnect(); fn(); }
    }, opts || { threshold: .3 });
    io.observe(el);
  }

  /* Hero typewriter: "|" = line break, "{…}" = accent run. */
  function typeH1(h) {
    if (h.dataset.ckOn) return; h.dataset.ckOn = '1';
    var text = h.dataset.text || '';
    var printable = text.replace(/[{}]/g, '').length;
    var caret = '<i class="dv-caret" aria-hidden="true"></i>';
    function render(n) {
      var out = '', inEm = false, shown = 0, buf = '';
      function flush() { if (!buf) return; out += inEm ? '<em>' + buf + '</em>' : buf; buf = ''; }
      for (var i = 0; i < text.length; i++) {
        var ch = text[i];
        if (ch === '{') { flush(); inEm = true; continue; }
        if (ch === '}') { flush(); inEm = false; continue; }
        if (shown >= n) break;
        shown++;
        if (ch === '|') { flush(); out += '<br>'; continue; }
        buf += ch.replace(/&/g, '&amp;').replace(/</g, '&lt;');
      }
      flush();
      h.innerHTML = out + caret;
      h.classList.toggle('is-done', n >= printable);
    }
    if (RM()) { render(printable); return; }
    render(0);
    var n = 0;
    setTimeout(function step() { n++; render(n); if (n < printable) setTimeout(step, 40); }, 260);
  }

  /* Count-ups: "300+" / "94%" style values. */
  function countUp(el, threshold) {
    if (el.dataset.ckOn) return; el.dataset.ckOn = '1';
    var m = String(el.dataset.value || el.textContent).match(/^(\D*)(\d+(?:\.\d+)?)(\D*)$/);
    if (!m) return;
    var target = parseFloat(m[2]);
    if (RM()) return;
    el.textContent = m[1] + '0' + m[3];
    once(el, function () {
      var t0 = performance.now();
      (function tick(t) {
        var p = Math.min(1, (t - t0) / 1400);
        el.textContent = m[1] + Math.round(target * (1 - Math.pow(1 - p, 3))) + m[3];
        if (p < 1) requestAnimationFrame(tick);
      })(t0);
    }, { threshold: threshold || .4 });
  }

  /* NPS ring: dash + number sweep up to the score. */
  function npsRing(card) {
    if (card.dataset.ckOn) return; card.dataset.ckOn = '1';
    var arc = card.querySelector('.ck-ring-arc'), num = card.querySelector('.ck-ring-val b');
    var val = +card.dataset.value || 0, C = 2 * Math.PI * 84;
    function set(v) { arc.setAttribute('stroke-dasharray', (C * v / 100) + ' ' + C); num.textContent = v; }
    if (RM() || !('IntersectionObserver' in window)) return;
    set(0);
    once(card, function () {
      var t0 = performance.now();
      (function tick(t) { var p = Math.min(1, (t - t0) / 1400); set(Math.round(val * (1 - Math.pow(1 - p, 3)))); if (p < 1) requestAnimationFrame(tick); })(t0);
    }, { threshold: .35 });
  }

  /* Review cards: spark burst from the stars + drifting particles on hover. */
  function reviewCard(card) {
    if (card.dataset.ckOn) return; card.dataset.ckOn = '1';
    card.addEventListener('mouseenter', function () {
      var st = card.querySelector('.mt-stars');
      window.MT_sparkBurst && window.MT_sparkBurst(card, st);
      window.MT_revParticles && window.MT_revParticles(card, 'start');
    });
    card.addEventListener('mouseleave', function () { window.MT_revParticles && window.MT_revParticles(card, 'stop'); });
  }

  /* Video testimonials carousel. */
  function voices(host) {
    if (host.dataset.ckOn) return; host.dataset.ckOn = '1';
    var data; try { data = JSON.parse(host.querySelector('.ck-voices-data').textContent); } catch (e) { return; }
    var n = data.length; if (!n) return;
    var track = host.querySelector('.ck-voice-track');
    var sec = host.closest('section');
    var idx = 0, shift = 0, noTrans = false, open = null, live = false, t1, t2;
    var play = '<span class="play"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"></path></svg></span>';
    var cols = [];
    for (var k = 0; k < 7; k++) {
      var col = document.createElement('div');
      col.className = 'ck-voice-col';
      col.innerHTML = '<button class="ck-voice-tile" type="button"><video class="ck-voice-vid" muted playsinline preload="metadata" tabindex="-1"></video><span class="veil"></span></button>';
      (function (d) { col.querySelector('button').addEventListener('click', function () { var rel = d - shift; d > shift ? move(1) : d < shift ? move(-1) : openModal(((idx + d) % n + n) % n); }); })(k - 3);
      track.appendChild(col); cols.push(col);
    }
    function mod(i) { return ((i % n) + n) % n; }
    function update() {
      track.style.transform = 'translateX(calc(-50% - 8px - ' + (shift * 320) + 'px))';
      track.style.transition = noTrans ? 'none' : 'transform .5s ' + EASE;
      cols.forEach(function (col, k) {
        var d = k - 3, center = d === shift, v = data[mod(idx + d)];
        col.classList.toggle('is-center', center);
        col.style.height = (center ? 606 : 494) + 'px';
        col.style.marginTop = (center ? 0 : 56) + 'px';
        col.style.transition = noTrans ? 'none' : 'height .5s ' + EASE + ',margin-top .5s ' + EASE;
        var vid = col.querySelector('video'), src = v.video + '#t=0.6';
        if (live && vid.getAttribute('src') !== src) vid.setAttribute('src', src);
        vid.setAttribute('aria-label', v.name ? 'Video review: ' + v.name + (v.role ? ', ' + v.role : '') : 'Video review');
        var btn = col.querySelector('button'), p = btn.querySelector('.play');
        if (center && !p) btn.insertAdjacentHTML('beforeend', play);
        if (!center && p) p.remove();
      });
    }
    function move(dir) {
      clearTimeout(t1); clearTimeout(t2);
      noTrans = false; shift = dir; update();
      t1 = setTimeout(function () {
        noTrans = true; idx = mod(idx + dir); shift = 0; update();
        t2 = setTimeout(function () { noTrans = false; update(); }, 30);
      }, 500);
    }
    function closeModal() { var m = sec.querySelector('.ck-voice-modal'); if (m) m.remove(); open = null; }
    function openModal(i) {
      closeModal(); open = i;
      var m = document.createElement('div');
      m.className = 'ck-voice-modal'; m.setAttribute('role', 'dialog'); m.setAttribute('aria-modal', 'true');
      m.innerHTML = '<div class="ck-voice-modal-card"><video class="ck-voice-player" controls autoplay playsinline></video><button class="close" type="button" aria-label="Close">' + host.dataset.closeIcon + '</button></div>';
      m.querySelector('video').src = data[i].video;
      m.addEventListener('click', closeModal);
      m.querySelector('.ck-voice-modal-card').addEventListener('click', function (e) { e.stopPropagation(); });
      m.querySelector('.close').addEventListener('click', closeModal);
      sec.appendChild(m);
    }
    window.addEventListener('keydown', function (e) { if (e.key === 'Escape' && open !== null) closeModal(); });
    update();
    once(host, function () { live = true; update(); }, { rootMargin: '600px 0px' });
    if (!RM()) setInterval(function () { if (open !== null || host.matches(':hover') || !live) return; move(1); }, 6200);
  }

  /* Kit rails: "next" pages the rail, then rewinds at the end. */
  function kitRail(row) {
    if (row.dataset.ckOn) return; row.dataset.ckOn = '1';
    var rail = row.querySelector('.ck-kitrail'), btn = row.querySelector('.ck-kitrow-next');
    if (!rail || !btn) return;
    var fwd = btn.querySelector('.ck-ic-next'), back = btn.querySelector('.ck-ic-rewind');
    function atEnd() { return rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8; }
    function sync() { var e = atEnd(); fwd.hidden = e; back.hidden = !e; }
    rail.addEventListener('scroll', sync, { passive: true });
    btn.addEventListener('click', function () { if (atEnd()) rail.scrollTo({ left: 0, behavior: 'smooth' }); else rail.scrollBy({ left: rail.clientWidth, behavior: 'smooth' }); });
    sync();
  }

  /* Training Monitor clip: plays (sped up to ~18s) only while visible. */
  function gif(el) {
    if (el.dataset.ckOn) return; el.dataset.ckOn = '1';
    var dur = +el.dataset.duration || 18;
    function rate() { if (el.duration && isFinite(el.duration)) el.playbackRate = Math.max(.1, el.duration / dur); }
    el.addEventListener('loadedmetadata', rate); rate();
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (es) { if (es[0].isIntersecting) { var p = el.play(); p && p.catch(function () {}); } else el.pause(); }, { rootMargin: '200px' }).observe(el);
  }

  /* Booking calendar: weekdays from two days out, current month + 2. */
  function calendar(box) {
    if (box.dataset.ckOn) return; box.dataset.ckOn = '1';
    var off = 0, day = null, slot = null;
    var label = box.querySelector('.ck-book-month b'), prev = box.querySelector('[data-cal-prev]'), next = box.querySelector('[data-cal-next]');
    var days = box.querySelector('.ck-book-days'), note = box.querySelector('.ck-book-note b');
    var slots = [].slice.call(box.querySelectorAll('.ck-book-slots button'));
    var confirmBox = box.querySelector('.cv-book-confirm');
    var meetUrl = box.dataset.meetingUrl || '';
    var okIcon = box.dataset.okIcon || '';
    function cal() {
      var now = new Date(), base = new Date(now.getFullYear(), now.getMonth() + off, 1);
      var y = base.getFullYear(), m = base.getMonth(), dim = new Date(y, m + 1, 0).getDate();
      var min = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2), open = [];
      for (var d = 1; d <= dim; d++) { var dt = new Date(y, m, d), w = dt.getDay(); if (dt >= min && w && w !== 6) open.push(d); }
      return { y: y, month: base.toLocaleString('en-US', { month: 'long' }), dim: dim, lead: (base.getDay() + 6) % 7, open: open };
    }
    function selDay(c) { return day && c.open.indexOf(day) > -1 ? day : c.open[0]; }
    function selLabel(c) { var d = selDay(c); return d ? d + ' ' + c.month + ' ' + c.y : '…'; }
    function renderConfirm(c) {
      confirmBox.innerHTML = '';
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'cv-confirm'; b.disabled = !slot;
      b.textContent = slot ? 'Confirm ' + slot : 'Pick a time to confirm';
      b.addEventListener('click', function () {
        var when = selLabel(c) + ' at ' + slot;
        if (meetUrl) { window.open(meetUrl, '_blank', 'noopener'); return; }
        /* No booking system behind this calendar: hand the chosen time to
           the contact form so the request actually reaches the team. */
        var form = box.closest('section').querySelector('.ck-bk-form');
        var msg = form && form.querySelector('textarea');
        if (msg) { msg.value = 'I\'d like to book a call on ' + when + ' (Eastern Time).' + (msg.value ? '\n\n' + msg.value : ''); }
        confirmBox.innerHTML = '<p class="cv-ok">' + okIcon + ' Requested: ' + when + '. Add your details and send. We\'ll confirm by email.</p>';
        var first = form && form.querySelector('input:not([type=hidden])');
        if (first) { first.focus({ preventScroll: true }); form.scrollIntoView({ behavior: RM() ? 'auto' : 'smooth', block: 'center' }); }
      });
      confirmBox.appendChild(b);
    }
    function render() {
      var c = cal(), s = selDay(c), h = '';
      label.textContent = c.month + ' ' + c.y;
      prev.disabled = off === 0; next.disabled = off >= 2;
      for (var i = 0; i < c.lead; i++) h += '<span class="is-blank"></span>';
      for (var d = 1; d <= c.dim; d++) {
        h += c.open.indexOf(d) > -1
          ? '<span role="button" tabindex="0" data-d="' + d + '" aria-pressed="' + (d === s) + '" aria-label="' + d + ' ' + c.month + '" class="is-open' + (d === s ? ' is-sel' : '') + '">' + d + '</span>'
          : '<span aria-disabled="true">' + d + '</span>';
      }
      days.innerHTML = h;
      note.textContent = selLabel(c);
      slots.forEach(function (b) { var on = b.textContent.trim() === slot; b.classList.toggle('is-sel', on); b.setAttribute('aria-pressed', on); });
      renderConfirm(c);
    }
    days.addEventListener('click', function (e) { var t = e.target.closest('[data-d]'); if (!t) return; day = +t.dataset.d; slot = null; render(); });
    days.addEventListener('keydown', function (e) { var t = e.target.closest('[data-d]'); if (t && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); day = +t.dataset.d; slot = null; render(); } });
    prev.addEventListener('click', function () { if (off > 0) { off--; day = null; slot = null; render(); } });
    next.addEventListener('click', function () { if (off < 2) { off++; day = null; slot = null; render(); } });
    slots.forEach(function (b) { b.addEventListener('click', function () { slot = b.textContent.trim(); render(); }); });
    render();
  }

  function init(root) {
    root.querySelectorAll('.ck-lp h1.dv-type[data-text]').forEach(typeH1);
    root.querySelectorAll('.ck-lp .dv-metric b').forEach(function (b) { countUp(b, .4); });
    root.querySelectorAll('.ck-lp .ck-bignum .ck-count').forEach(function (b) { countUp(b, .4); });
    root.querySelectorAll('.ck-lp .ck-ring-card').forEach(npsRing);
    root.querySelectorAll('.ck-lp .ck-rv2').forEach(reviewCard);
    root.querySelectorAll('.ck-lp .ck-voices').forEach(voices);
    root.querySelectorAll('.ck-lp .ck-kitrow').forEach(kitRail);
    root.querySelectorAll('.ck-lp .ck-fr2-gif').forEach(gif);
    root.querySelectorAll('.ck-lp .ck-bk-meet').forEach(calendar);

    /* .dv-in (threshold .12), in-view blocks (.3), section reveals */
    root.querySelectorAll('.ck-lp .dv-in:not(.is-in)').forEach(function (el) { once(el, function () { el.classList.add('is-in'); }, { threshold: .12 }); });
    root.querySelectorAll('.ck-lp [data-ck-inview]:not(.is-in)').forEach(function (el) { once(el, function () { el.classList.add('is-in'); }, { threshold: .3 }); });
    if (!RM()) {
      root.querySelectorAll('.ck-lp section.cv-rv:not(.is-rv)').forEach(function (s) {
        if (s.getBoundingClientRect().top < innerHeight) s.classList.add('is-rv');
        else once(s, function () { s.classList.add('is-rv'); }, { threshold: .08, rootMargin: '0px 0px -40px 0px' });
      });
    } else {
      root.querySelectorAll('.ck-lp section.cv-rv').forEach(function (s) { s.classList.add('is-rv'); });
    }

    /* back to top */
    root.querySelectorAll('.ck-lp .ck-to-top').forEach(function (b) {
      if (b.dataset.ckOn) return; b.dataset.ckOn = '1';
      function h() { b.classList.toggle('is-on', window.scrollY > 600); }
      h(); window.addEventListener('scroll', h, { passive: true });
      b.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: RM() ? 'auto' : 'smooth' }); });
    });

    /* original runtime: .mt-reveal, kit-rail bento spotlight */
    window.MT_initReveal && window.MT_initReveal(root);
    window.MT_initBento && window.MT_initBento(root);
  }

  function boot() { if (document.querySelector('.ck-lp')) init(document); }
  if (document.readyState !== 'loading') boot(); else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', function (e) { if (e.target.querySelector('.ck-lp')) init(e.target); });
})();
}
